/**
 * Zustand store — UI-only state (never serialised to server).
 * Server state lives in TanStack Query cache.
 */

import { useEffect } from 'react'
import { create } from 'zustand'
import {
  DEFAULT_READ_ALOUD_SPEED,
  DEFAULT_READ_ALOUD_VOICE,
  normalizeReadAloudSpeed,
  normalizeReadAloudVoice,
  type ReadAloudVoice,
} from '../lib/readAloudSettings'

export type PaneMode = 'auto' | 'list-only' | 'chat-only' | 'split'
export type ComposeMode = 'queue' | 'force-immediate'

/**
 * Foldable / responsive viewport hook.
 *
 * Listens to Visual Viewport and (when supported) viewport-segment changes.
 * In `auto` paneMode, switches between split and stacked based on width.
 * Samsung foldables can report ~360px folded and ~600-960px unfolded
 * depending on browser chrome, zoom, and orientation.
 */
export function useFoldableViewport() {
  const setEffectiveWidth = useUi.getState().setEffectiveWidth
  useEffect(() => {
    function update() {
      const w = window.visualViewport?.width ?? window.innerWidth
      setEffectiveWidth(Math.round(w))
    }
    update()
    window.visualViewport?.addEventListener('resize', update)
    window.addEventListener('resize', update)
    window.addEventListener('orientationchange', update)
    return () => {
      window.visualViewport?.removeEventListener('resize', update)
      window.removeEventListener('resize', update)
      window.removeEventListener('orientationchange', update)
    }
  }, [setEffectiveWidth])
}

/**
 * Returns the current effective pane mode (resolves 'auto' to 'split'
 * when the viewport is wide enough, else 'chat-only' or 'list-only').
 */
export function useResolvedPaneMode(): Exclude<PaneMode, 'auto'> {
  const pane = useUi((s) => s.paneMode)
  const w = useUi((s) => s.effectiveWidth)
  if (pane !== 'auto') return pane
  // Auto: split on tablet+ (foldable open), else chat-only when in chat
  // route. The route-aware part lives in AppShell layout decisions.
  if (w >= 560) return 'split'
  return 'chat-only'
}

/** A pending Web-Share-Target file intake — files (and optional text) that
 *  arrived from Android's share sheet. Surfaced on the chat list as a
 *  "Tap a chat to attach" banner; consumed by the next Composer that
 *  mounts after the user picks a chat. Text-only shares bypass this UI store
 *  path and open an X Links new-topic draft directly. */
export type PendingShareSnapshot = {
  /** IDB row id (so the consumer can delete the row after staging). */
  id: number
  /** Files (one or many, but typically a single image / screenshot). */
  files: File[]
  /** Title + text + url joined and trimmed; pre-fills composer text. */
  text: string
}

type UiState = {
  paneMode: PaneMode
  setPaneMode: (m: PaneMode) => void

  chatFullWindow: boolean
  setChatFullWindow: (enabled: boolean) => void

  effectiveWidth: number
  setEffectiveWidth: (w: number) => void

  textSizeScale: number
  setTextSizeScale: (s: number) => void

  readAloudAuto: boolean
  setReadAloudAuto: (enabled: boolean) => void
  readAloudAutoListen: boolean
  setReadAloudAutoListen: (enabled: boolean) => void
  readAloudSpeed: number
  setReadAloudSpeed: (speed: number) => void
  readAloudVoice: ReadAloudVoice
  setReadAloudVoice: (voice: ReadAloudVoice) => void

  draftsByChat: Record<string, string>
  draftUpdatedAt: Record<string, number>
  setDraft: (chatId: string, text: string) => void
  clearDraft: (chatId: string) => void

  composeModeByChat: Record<string, ComposeMode>
  setComposeMode: (chatId: string, m: ComposeMode) => void

  pendingShare: PendingShareSnapshot | null
  setPendingShare: (s: PendingShareSnapshot | null) => void
}

const STORAGE_KEY = 'lane-control-ui'
const TOPIC_DRAFT_SEPARATOR = '::topic::'

export function draftTargetKey(chatId: string, topicId?: string | null): string {
  return topicId ? `${chatId}${TOPIC_DRAFT_SEPARATOR}${topicId}` : chatId
}

export function isVisibleDraft(text: string | null | undefined): boolean {
  return !!text && text.trim().length > 0
}

export function chatHasDraft(drafts: Record<string, string>, chatId: string): boolean {
  const prefix = `${chatId}${TOPIC_DRAFT_SEPARATOR}`
  return Object.entries(drafts).some(([key, text]) => (
    (key === chatId || key.startsWith(prefix)) && isVisibleDraft(text)
  ))
}

export function firstDraftForChat(drafts: Record<string, string>, chatId: string): string | null {
  const direct = drafts[chatId]
  if (isVisibleDraft(direct)) return direct
  const prefix = `${chatId}${TOPIC_DRAFT_SEPARATOR}`
  const topicDraft = Object.entries(drafts).find(([key, text]) => (
    key.startsWith(prefix) && isVisibleDraft(text)
  ))
  return topicDraft?.[1] ?? null
}

export function latestDraftAtForChat(
  drafts: Record<string, string>,
  draftUpdatedAt: Record<string, number>,
  chatId: string,
): number {
  const prefix = `${chatId}${TOPIC_DRAFT_SEPARATOR}`
  let latest = 0
  for (const [key, text] of Object.entries(drafts)) {
    if ((key === chatId || key.startsWith(prefix)) && isVisibleDraft(text)) {
      latest = Math.max(latest, draftUpdatedAt[key] || 0)
    }
  }
  return latest
}

function persisted<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return fallback
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

let draftPersistTimer: number | null = null
let draftPersistDrafts: Record<string, string> | null = null
let draftPersistTimes: Record<string, number> | null = null

function cancelDraftSnapshot() {
  if (draftPersistTimer !== null) {
    window.clearTimeout(draftPersistTimer)
    draftPersistTimer = null
  }
  draftPersistDrafts = null
  draftPersistTimes = null
}

function writeDraftSnapshot(
  drafts: Record<string, string>,
  draftTimes: Record<string, number>,
) {
  localStorage.setItem(`${STORAGE_KEY}:drafts`, JSON.stringify(drafts))
  localStorage.setItem(`${STORAGE_KEY}:draftTimes`, JSON.stringify(draftTimes))
}

function flushDraftSnapshot() {
  if (draftPersistTimer !== null) {
    window.clearTimeout(draftPersistTimer)
    draftPersistTimer = null
  }
  if (!draftPersistDrafts || !draftPersistTimes) return
  writeDraftSnapshot(draftPersistDrafts, draftPersistTimes)
  draftPersistDrafts = null
  draftPersistTimes = null
}

function scheduleDraftSnapshot(
  drafts: Record<string, string>,
  draftTimes: Record<string, number>,
) {
  draftPersistDrafts = drafts
  draftPersistTimes = draftTimes
  if (typeof window === 'undefined') {
    writeDraftSnapshot(drafts, draftTimes)
    draftPersistDrafts = null
    draftPersistTimes = null
    return
  }
  if (draftPersistTimer !== null) window.clearTimeout(draftPersistTimer)
  draftPersistTimer = window.setTimeout(flushDraftSnapshot, 250)
}

function writeDraftSnapshotNow(
  drafts: Record<string, string>,
  draftTimes: Record<string, number>,
) {
  cancelDraftSnapshot()
  writeDraftSnapshot(drafts, draftTimes)
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flushDraftSnapshot)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushDraftSnapshot()
  })
}

export const useUi = create<UiState>((set, get) => ({
  paneMode: persisted<PaneMode>(`${STORAGE_KEY}:paneMode`, 'auto'),
  setPaneMode: (m) => {
    localStorage.setItem(`${STORAGE_KEY}:paneMode`, JSON.stringify(m))
    set({ paneMode: m })
  },

  chatFullWindow: persisted<boolean>(`${STORAGE_KEY}:chatFullWindow`, false),
  setChatFullWindow: (enabled) => {
    localStorage.setItem(`${STORAGE_KEY}:chatFullWindow`, JSON.stringify(enabled))
    set({ chatFullWindow: enabled })
  },

  effectiveWidth: typeof window !== 'undefined'
    ? Math.round(window.visualViewport?.width ?? window.innerWidth)
    : 1024,
  setEffectiveWidth: (w) => set({ effectiveWidth: w }),

  textSizeScale: persisted<number>(`${STORAGE_KEY}:textSize`, 1.0),
  setTextSizeScale: (s) => {
    localStorage.setItem(`${STORAGE_KEY}:textSize`, JSON.stringify(s))
    set({ textSizeScale: s })
  },

  readAloudAuto: persisted<boolean>(`${STORAGE_KEY}:readAloudAuto`, false),
  setReadAloudAuto: (enabled) => {
    localStorage.setItem(`${STORAGE_KEY}:readAloudAuto`, JSON.stringify(enabled))
    set({ readAloudAuto: enabled })
  },
  readAloudAutoListen: persisted<boolean>(`${STORAGE_KEY}:readAloudAutoListen`, false),
  setReadAloudAutoListen: (enabled) => {
    localStorage.setItem(`${STORAGE_KEY}:readAloudAutoListen`, JSON.stringify(enabled))
    set({ readAloudAutoListen: enabled })
  },
  readAloudSpeed: normalizeReadAloudSpeed(
    persisted<unknown>(`${STORAGE_KEY}:readAloudSpeed`, DEFAULT_READ_ALOUD_SPEED),
  ),
  setReadAloudSpeed: (value) => {
    const speed = normalizeReadAloudSpeed(value)
    localStorage.setItem(`${STORAGE_KEY}:readAloudSpeed`, JSON.stringify(speed))
    set({ readAloudSpeed: speed })
  },
  readAloudVoice: normalizeReadAloudVoice(
    persisted<unknown>(`${STORAGE_KEY}:readAloudVoice`, DEFAULT_READ_ALOUD_VOICE),
  ),
  setReadAloudVoice: (value) => {
    const voice = normalizeReadAloudVoice(value)
    localStorage.setItem(`${STORAGE_KEY}:readAloudVoice`, JSON.stringify(voice))
    set({ readAloudVoice: voice })
  },

  draftsByChat: persisted<Record<string, string>>(`${STORAGE_KEY}:drafts`, {}),
  draftUpdatedAt: persisted<Record<string, number>>(`${STORAGE_KEY}:draftTimes`, {}),
  setDraft: (chatId, text) => {
    const next = { ...get().draftsByChat }
    const nextTimes = { ...get().draftUpdatedAt }
    if (text.length === 0) {
      delete next[chatId]
      delete nextTimes[chatId]
    } else {
      next[chatId] = text
      nextTimes[chatId] = Date.now()
    }
    scheduleDraftSnapshot(next, nextTimes)
    set({ draftsByChat: next, draftUpdatedAt: nextTimes })
  },
  clearDraft: (chatId) => {
    const next = { ...get().draftsByChat }
    const nextTimes = { ...get().draftUpdatedAt }
    delete next[chatId]
    delete nextTimes[chatId]
    writeDraftSnapshotNow(next, nextTimes)
    set({ draftsByChat: next, draftUpdatedAt: nextTimes })
  },

  composeModeByChat: persisted<Record<string, ComposeMode>>(
    `${STORAGE_KEY}:compose`, {}),
  setComposeMode: (chatId, m) => {
    const next = { ...get().composeModeByChat, [chatId]: m }
    localStorage.setItem(`${STORAGE_KEY}:compose`, JSON.stringify(next))
    set({ composeModeByChat: next })
  },

  // Pending share — never persisted (the IDB row is the source of truth;
  // store is just a hot pointer that survives navigation between the
  // chat list banner and the chosen chat's composer).
  pendingShare: null,
  setPendingShare: (s) => set({ pendingShare: s }),
}))

/** Apply one shared draft atomically, cancelling any older local disk snapshot. */
export function applySharedDraft(target: string, text: string, updatedAt: number) {
  const state = useUi.getState()
  const drafts = { ...state.draftsByChat }
  const times = { ...state.draftUpdatedAt }
  if (text) {
    drafts[target] = text
    times[target] = updatedAt
  } else {
    delete drafts[target]
    delete times[target]
  }
  writeDraftSnapshotNow(drafts, times)
  useUi.setState({ draftsByChat: drafts, draftUpdatedAt: times })
}
